import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { Box, Button, Step, StepButton, Stepper, Tooltip } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';

import { useNavigate } from 'react-router';
import { getDataProcessor, getNodeStatus } from "../../api's/DataProcessorApi";
import { getWorkflowById } from "../../api's/WorkflowApi";
import { WorkflowContext } from '../../contexts/WorkflowProvider';
import { getAllInChainWorkflowApi } from "../../api's/InChainWorkflowApi";
import LoadingIcon from '../../reusable-component/LoadingIcon';

const WorkflowStepper = () => {
  // Current step state
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { workflow } = useContext(WorkflowContext);
  const [workflows, setWorkflows] = useState([]);
  const [workflowNames, setWorkflowNames] = useState([]);

  // Function to navigate to workflow
  const navigateToWorkflow = async (workflow_id) => {
    sessionStorage.removeItem('allNodes');
    sessionStorage.removeItem('node');
    sessionStorage.removeItem('elementCount');
    sessionStorage.removeItem('saved_node');
    sessionStorage.removeItem('edges');
    sessionStorage.removeItem('workflowStepper');
    setLoading(true);

    const response = await getWorkflowById(workflow_id);

    let result, nodeStatus;
    try {
      result = await getDataProcessor(workflow_id, 'Batch');
      nodeStatus = await getNodeStatus(
        response.data.engagement.client.client_name,
        response.data.engagement.engagement_name,
        response.data.workflow_name,
        response.data.cloudEnvironment
      );
    } catch (error) {
      console.error(error);
    }

    if (response.status === 200 && result) {
      let elemCount = 0;
      const newSavedElement = [];

      result.data.nodes.forEach((el) => {
        el['nodeId'] = `${el.nodeId}`;
        elemCount++;
        newSavedElement.push(el.id);
        el.nodeId;
      });

      sessionStorage.setItem('allNodes', JSON.stringify(result.data.form_data));
      sessionStorage.setItem('elementCount', elemCount);
      sessionStorage.setItem('node', JSON.stringify(result.data.nodes));
      sessionStorage.setItem('edges', JSON.stringify(result.data.edges));
      sessionStorage.setItem('saved_node', JSON.stringify(newSavedElement));
      nodeStatus && sessionStorage.setItem('nodeStatus', JSON.stringify(nodeStatus.data));

      sessionStorage.setItem('workflowStepper', JSON.stringify(workflow_id));

      // const url = `/workflow-${processType}-${processWorkflowType}/onboarding?processType=${processType}`;
      const url = `/workflow-generic-generic/onboarding?processType=Generic`;

      // Navigate to the workflow page
      navigate(url, {
        state: {
          workflow_name: response.data.workflow_name,
          client_name: response.data.engagement.client.client_name,
          engagement_name: response.data.engagement.engagement_name,
          execution_mode: 'Batch',
          created_by: 'DEP-ADMIN',
          created_at: response.data.creation_date,
          status: response.data.status,
          workflow_data: response.data,
        },
      });
    }
    setLoading(false);
  };

  // Function to handle step click
  const handleStepClick = (stepIndex) => {
    setActiveStep(stepIndex);

    const selectedWorkflowId = workflows[stepIndex];
    navigateToWorkflow(selectedWorkflowId); // Use the navigate function
  };

  const fetchAllInChainWorkflows = async () => {
    setLoading(true);
    try {
      const response = await getAllInChainWorkflowApi();
      if (response.status === 200) {
        const filteredData = response.data.find((item) => item.engagement_id === workflow?.engagement?.engagement_id);
        if (filteredData) {
          const filtered_workflow_id = [];
          const filtered_workflow_name = [];

          filteredData.inChain.forEach((item, index) => {
            // if (item === true) {
            filtered_workflow_id.push(filteredData.workflow_id[index]);
            filtered_workflow_name.push(filteredData.workflow_name[index]);
            // }
          });
          setWorkflows(filtered_workflow_id);
          setWorkflowNames(filtered_workflow_name);
          const targetIndex = filtered_workflow_id.findIndex((workflowId) => workflowId === workflow?.workflow_id);
          if (targetIndex !== -1) {
            setActiveStep(targetIndex);
          }
        } else {
          setWorkflows([]);
          setWorkflowNames([]);
        }
      }
    } catch (error) {
      setLoading(false);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAllInChainWorkflows();
  }, [workflow.engagement]);

  const getMinWidth = (length) => {
    if (length < 2) return '200px';
    if (length < 5) return '300px';
    if (length < 10) return '700px';
    return '1000px'; // Default case for 20 or more workflows
  };

  return (
    <>
      {workflows.length > 1 && workflows.includes(workflow.workflow_id) && (
        <Box
          sx={{
            width: `${workflows.length < 5 ? '30%' : workflows.length < 10 ? '50%' : '70%'}`,
            maxWidth: '60%', // Max width of 60% of the parent container
            display: 'flex',
            justifyContent: 'center',
            margin: '0 auto', // Center the Box
          }}
        >
          <Button
            disabled={activeStep === 0}
            variant="contained"
            size="small"
            onClick={() => handleStepClick(activeStep - 1)}
            sx={{ padding: '2px', fontSize: '0.8rem', height: '100%' }}
          >
            PREV
          </Button>
          <Box
            sx={{
              overflowX: 'auto', // Enable horizontal scroll if content overflows
              overflowY: 'hidden', // Hide vertical overflow
              whiteSpace: 'nowrap', // Prevent content wrapping
              padding: '0 0 8px 0',
              margin: '0 8px',
              '&::-webkit-scrollbar': {
                height: '5px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#bbb',
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
              },
              '&::-webkit-scrollbar-track-piece:end ': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-track-piece:start': {
                background: 'transparent',
              },
              '&::-webkit-resizer': {
                background: 'transparent',
              },
            }}
          >
            <Stepper
              nonLinear
              activeStep={activeStep}
              sx={{
                minWidth: getMinWidth(workflows.length),
                [`& .MuiStep-root`]: {
                  flexDirection: 'column-reverse',
                  '&::after': {
                    top: 'unset',
                    bottom: 'calc(var(--StepIndicator-size) / 2 - var(--Step-connectorThickness) / 2)',
                  },
                },
                [`& .MuiStep-completed::after`]: {
                  bgcolor: 'primary.500',
                },
                [`& .MuiStep-active .MuiStepIndicator-root`]: {
                  borderColor: 'primary.500',
                },
                [`& .MuiStep-root:has(+ .MuiStep-active)::after`]: {
                  color: 'primary.500',
                  backgroundColor: 'transparent',
                  backgroundImage: 'radial-gradient(currentColor 2px, transparent 2px)',
                  backgroundSize: '7px 7px',
                  backgroundPosition: 'center left',
                },
                [`& .MuiStep-disabled *`]: {
                  color: 'neutral.plainDisabledColor',
                },
              }}
            >
              {workflows?.map((workflowId, index) => (
                <Step
                  key={workflowId}
                  completed={activeStep > index}
                  disabled={loading}
                  orientation="vertical"
                  indicator={
                    activeStep > index ? <CheckRoundedIcon color="primary" /> : <KeyboardArrowDownRoundedIcon />
                  }
                >
                  <Tooltip
                    title={`${workflowNames[index]}`}
                    //   arrow
                    PopperProps={{
                      modifiers: [
                        {
                          name: 'offset',
                          options: {
                            offset: [0, -28],
                          },
                        },
                      ],
                    }}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: 'white',
                          color: 'black',
                          border: '1px solid #ccc',
                          boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                          fontSize: '0.875rem',
                          borderRadius: '4px',
                        },
                      },
                    }}
                  >
                    <StepButton onClick={() => handleStepClick(index)} disabled={loading}>
                      {/* {loading ? <CircularProgress size={24} /> : `Workflow ${index + 1}`} */}
                    </StepButton>
                  </Tooltip>
                </Step>
              ))}
            </Stepper>
          </Box>
          <Button
            disabled={activeStep === workflows.length - 1}
            onClick={() => handleStepClick(activeStep + 1)}
            variant="contained"
            size="small"
            sx={{ padding: '2px', fontSize: '0.8rem', height: '100%' }}
          >
            NEXT
          </Button>
          {loading && <LoadingIcon />}
        </Box>
      )}
    </>
  );
};

export default WorkflowStepper;
